/**
 * Los dos hooks con los que se pide dato en este dashboard.
 *
 *   `useApiQuery`     un GET que devuelve un objeto
 *   `useKeysetQuery`  un GET que devuelve una lista paginada por cursor
 *
 * No hay un tercero. Si un módulo necesita `useEffect` + `fetch`, es que
 * falta un hook acá — pedirlo, no escribirlo a mano: a mano se pierden cache,
 * deduplicación, reintentos y el manejo de 401.
 */
import {
  useInfiniteQuery,
  useQuery,
  type QueryKey,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import { useApi } from './useApi';
import type { ApiError } from './errors';
import type { Page, QueryParams } from './types';

export interface ApiQueryOptions {
  /** `false` mientras falte un dato (un id de la ruta, un filtro sin elegir). */
  enabled?: boolean;
  staleTime?: number;
  refetchInterval?: number;
}

/**
 * GET tipado contra el OpenAPI.
 *
 *     const q = useApiQuery<Session>(authKeys.session(), '/api/v1/auth/me');
 *
 * El tipo `T` sale de `@/api/types` o de `components['schemas'][...]`.
 */
export function useApiQuery<T>(
  queryKey: QueryKey,
  path: string,
  query?: QueryParams,
  options: ApiQueryOptions = {},
): UseQueryResult<T, ApiError> {
  const api = useApi();
  return useQuery<T, ApiError>({
    queryKey,
    queryFn: ({ signal }) => api.get<T>(path, query, { signal }),
    ...options,
  });
}

export interface KeysetQueryOptions extends ApiQueryOptions {
  /** Filas por página. La API acepta hasta 200. */
  limit?: number;
}

export interface KeysetResult<TItem> {
  /** Todas las páginas ya traídas, aplanadas. */
  items: TItem[];
  query: UseInfiniteQueryResult<{ pages: Page<TItem>[] }, ApiError>;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
}

/**
 * Lista paginada por **keyset**, alineada con `services/api/pagination.py`.
 *
 *     const users = useKeysetQuery<User>(authKeys.users(50), '/api/v1/auth/users');
 *     …
 *     {users.hasMore && <Button onClick={users.loadMore}>Cargar más</Button>}
 *
 * No existe "ir a la página 7" y no debería existir: el backend no usa
 * `OFFSET` a propósito (sobre `scan_event` la página 20 costaría veinte veces
 * la página 1). El patrón de UI correcto es "cargar más" o scroll infinito.
 *
 * El `cursor` es opaco y viene del servidor; nunca lo construyas.
 */
export function useKeysetQuery<TItem>(
  queryKey: QueryKey,
  path: string,
  query?: QueryParams,
  options: KeysetQueryOptions = {},
): KeysetResult<TItem> {
  const api = useApi();
  const { limit = 50, ...queryOptions } = options;

  const result = useInfiniteQuery<
    Page<TItem>,
    ApiError,
    { pages: Page<TItem>[] },
    QueryKey,
    string | null
  >({
    queryKey,
    queryFn: ({ pageParam, signal }) =>
      api.get<Page<TItem>>(path, { ...query, limit, cursor: pageParam }, { signal }),
    initialPageParam: null,
    // `has_more` y `next_cursor` son redundantes a propósito en la API;
    // `next_cursor` es el que manda: `has_more: true` sin cursor sería un bug
    // del backend y un bucle infinito acá.
    getNextPageParam: (last) => last.next_cursor,
    ...queryOptions,
  });

  const items = useMemo(
    () => (result.data?.pages ?? []).flatMap((page) => page.items),
    [result.data],
  );

  return {
    items,
    query: result,
    hasMore: result.hasNextPage,
    isLoadingMore: result.isFetchingNextPage,
    loadMore: () => {
      if (result.hasNextPage && !result.isFetchingNextPage) void result.fetchNextPage();
    },
  };
}
